import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import throttle from 'lodash/throttle';
import isString from 'lodash/isString';
import ConfigContext from 'choerodon-ui/lib/config-provider/ConfigContext';
import { Size } from 'choerodon-ui/lib/_util/enum';
import Button from '../button/Button';
import { FuncType } from '../button/enum';
import Picture from './Picture';
import transform, { toTransformValue } from '../_util/transform';
import EventManager from '../_util/EventManager';
import Toolbar from './Toolbar';
import Navbar from './Navbar';
const scaleSteps = [
    0.05, 0.06, 0.07, 0.09, 0.11, 0.14, 0.17, 0.21, 0.26, 0.32, 0.39, 0.47, 0.5, 0.57, 0.69, 0.83, 0.92,
    1, 1.2, 1.72, 2, 2.4, 2.88, 3.45, 4.14, 4.96, 5.95, 7.14, 8.56, 10,
];
function getPreviewItem(item) {
    if (isString(item)) {
        return {
            src: item,
        };
    }
    return item;
}
const PictureViewer = function PictureViewer(props) {
    const { list, defaultIndex = 0, prefixCls, modal } = props;
    const { getProPrefixCls } = useContext(ConfigContext);
    const pictureRef = useRef(null);
    const transformTargetRef = useRef(null);
    const [index, setIndex] = useState(defaultIndex);
    const [rotate, setRotate] = useState(0);
    const [translate, setTranslate] = useState([0, 0]);
    const [scale, setScale] = useState();
    const handleIndexChange = useCallback((newIndex) => {
        setIndex(newIndex);
        setTranslate([0, 0]);
        setRotate(0);
        setScale(undefined);
    }, []);
    const getImageNaturalScale = useCallback(() => {
        const { current } = pictureRef;
        if (current) {
            const image = current.getImage();
            if (image) {
                const { clientWidth, clientHeight, naturalHeight, naturalWidth } = image;
                const imageScale = Math.min(clientWidth / naturalWidth, clientHeight / naturalHeight);
                return scaleSteps.findIndex((step, index, steps) => {
                    const nextStep = steps[index + 1];
                    return step === imageScale ||
                        (imageScale < step && index === 0) ||
                        (step < imageScale && nextStep > imageScale) ||
                        (nextStep < imageScale && index === steps.length - 1);
                });
            }
        }
        return scaleSteps.indexOf(1);
    }, [pictureRef]);
    const getCurrentScale = useCallback(() => {
        if (scale !== undefined) {
            return scale;
        }
        return getImageNaturalScale();
    }, [getImageNaturalScale, scale]);
    const customizedPrefixCls = getProPrefixCls('picture-viewer', prefixCls);
    const handlePrev = useCallback(() => handleIndexChange(index - 1), [index]);
    const handleNext = useCallback(() => handleIndexChange(index + 1), [index]);
    const handleClose = useCallback(() => modal && modal.close(), []);
    const handleRotateLeft = useCallback(() => setRotate((rotate - 90) % 360), [rotate]);
    const handleRotateRight = useCallback(() => setRotate((rotate + 90) % 360), [rotate]);
    const handleZoomIn = useCallback(() => {
        const currentScale = getCurrentScale();
        if (currentScale < scaleSteps.length - 1) {
            setScale(getCurrentScale() + 1);
        }
    }, [getCurrentScale]);
    const handleZoomOut = useCallback(() => {
        const currentScale = getCurrentScale();
        if (currentScale > 0) {
            setScale(currentScale - 1);
        }
    }, [getCurrentScale]);
    const throttleWheel = useMemo(() => throttle((callback) => callback(), 60), []);
    const handleWheel = useCallback((e) => {
        if (e.deltaX > 0 || e.deltaY > 0) {
            throttleWheel(handleZoomOut);
        }
        else {
            throttleWheel(handleZoomIn);
        }
    }, [handleZoomOut, handleZoomIn]);
    const translateEvent = useMemo(() => new EventManager(), []);
    const executeTransform = useCallback((target, r, s, t) => {
        const transformValue = toTransformValue({
            translate: `${t[0]}px,${t[1]}px`,
            rotate: r ? `${r}deg` : undefined,
            scale: s !== undefined && s > -1 ? scaleSteps[s] / scaleSteps[getImageNaturalScale()] : undefined,
        });
        transform(transformValue, target.style);
    }, []);
    const handleMouseDown = useCallback((e) => {
        const { current } = transformTargetRef;
        if (current) {
            const { pageX, pageY, currentTarget } = e;
            currentTarget.style.cursor = 'grabbing';
            let [currentX, currentY] = translate;
            const startX = currentX - pageX;
            const startY = currentY - pageY;
            const handleMouseMove = (me) => {
                currentX = startX + me.pageX;
                currentY = startY + me.pageY;
                executeTransform(current, rotate, scale, [currentX, currentY]);
            };
            const handleMouseUp = () => {
                currentTarget.style.cursor = '';
                setTranslate([currentX, currentY]);
                translateEvent
                    .removeEventListener('mousemove', handleMouseMove)
                    .removeEventListener('mouseup', handleMouseUp);
            };
            translateEvent
                .setTarget(document)
                .addEventListener('mousemove', handleMouseMove)
                .addEventListener('mouseup', handleMouseUp);
        }
    }, [translate, rotate, scale]);
    useEffect(() => {
        const { current } = transformTargetRef;
        if (current) {
            executeTransform(current, rotate, scale, translate);
        }
    }, [scale, rotate, translate]);
    useEffect(() => () => {
        translateEvent.clear();
    }, []);
    const { length } = list;
    if (length) {
        const { src, downloadUrl } = getPreviewItem(list[index]);
        return (React.createElement("div", { className: customizedPrefixCls, onWheel: handleWheel },
            length > 1 && (React.createElement(Button, { icon: "navigate_before", disabled: index === 0, funcType: FuncType.link, onClick: handlePrev, className: `${customizedPrefixCls}-btn ${customizedPrefixCls}-btn-nav`, size: Size.large })),
            React.createElement("div", { className: `${customizedPrefixCls}-picture`, onMouseDown: handleMouseDown },
                React.createElement("div", { className: `${customizedPrefixCls}-picture-main`, ref: transformTargetRef },
                    React.createElement(Picture, { src: src, ref: pictureRef, objectFit: "scale-down", status: "loaded", preview: false, lazy: false, draggable: false })),
                React.createElement(Toolbar, { prefixCls: customizedPrefixCls, zoomInDisabled: scale === scaleSteps.length - 1, zoomOutDisabled: scale === 0, onZoomIn: handleZoomIn, onZoomOut: handleZoomOut, onRotateLeft: handleRotateLeft, onRotateRight: handleRotateRight, downloadUrl: downloadUrl }),
                React.createElement(Navbar, { prefixCls: customizedPrefixCls, value: index, onChange: handleIndexChange, list: list })),
            length > 1 && (React.createElement(Button, { icon: "navigate_next", disabled: index === length - 1, funcType: FuncType.link, onClick: handleNext, className: `${customizedPrefixCls}-btn ${customizedPrefixCls}-btn-nav`, size: Size.large })),
            React.createElement(Button, { icon: "close", funcType: FuncType.link, onClick: handleClose, className: `${customizedPrefixCls}-btn ${customizedPrefixCls}-btn-close` })));
    }
    return null;
};
PictureViewer.displayName = 'PictureViewer';
export default PictureViewer;
